import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { FUSEAU } from './domaine'

// Jours civils (AAAA-MM-JJ) et jours ouvrés. Calculs en UTC sur des dates sans heure :
// aucun décalage horaire ne peut déplacer un jour.

function versDate(jour: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour)
  if (!m) throw new RangeError(`Jour invalide : ${jour}`)
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}

function versJour(date: Date): string {
  return date.toISOString().slice(0, 10)
}

export function ajouterJours(jour: string, n: number): string {
  const d = versDate(jour)
  d.setUTCDate(d.getUTCDate() + n)
  return versJour(d)
}

/** Ajoute n mois ; le jour est ramené au dernier jour du mois d'arrivée s'il n'existe pas. */
export function ajouterMois(jour: string, n: number): string {
  const d = versDate(jour)
  const index = d.getUTCFullYear() * 12 + d.getUTCMonth() + n
  const annee = Math.floor(index / 12)
  const mois = index % 12
  const dernier = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate()
  return versJour(new Date(Date.UTC(annee, mois, Math.min(d.getUTCDate(), dernier))))
}

/** Nombre de jours de a à b (négatif si b précède a). */
export function ecartJours(a: string, b: string): number {
  return Math.round((versDate(b).getTime() - versDate(a).getTime()) / 86_400_000)
}

/** 0 dimanche … 6 samedi. */
export function jourDeLaSemaine(jour: string): number {
  return versDate(jour).getUTCDay()
}

/** Lundi de la semaine du jour (les semaines commencent le lundi). */
export function lundiDeLaSemaine(jour: string): string {
  return ajouterJours(jour, -((jourDeLaSemaine(jour) + 6) % 7))
}

/** Numéro de semaine ISO 8601 : la semaine 1 est celle qui contient le premier jeudi de l'année. */
export function numeroSemaine(jour: string): number {
  const jeudi = ajouterJours(lundiDeLaSemaine(jour), 3)
  return Math.floor(ecartJours(`${jeudi.slice(0, 4)}-01-01`, jeudi) / 7) + 1
}

/** Dimanche de Pâques (algorithme de Meeus, calendrier grégorien). */
export function paques(annee: number): string {
  const a = annee % 19
  const b = Math.floor(annee / 100)
  const c = annee % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mois = Math.floor((h + l - 7 * m + 114) / 31)
  const jourDuMois = ((h + l - 7 * m + 114) % 31) + 1
  return `${annee}-${String(mois).padStart(2, '0')}-${String(jourDuMois).padStart(2, '0')}`
}

/** Jours fériés de France métropolitaine, triés. */
export function joursFeriesFrancais(annee: number): string[] {
  const p = paques(annee)
  const fixes = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25']
  return [
    ...fixes.map((md) => `${annee}-${md}`),
    ajouterJours(p, 1), // lundi de Pâques
    ajouterJours(p, 39), // Ascension
    ajouterJours(p, 50), // lundi de Pentecôte
  ].sort()
}

/** Un calendrier dit quels jours sont ouvrés. */
export interface CalendrierOuvre {
  readonly nom: string
  estOuvre(jour: string): boolean
  /** Implémentation dont les règles restent à vérifier auprès d'une source officielle. */
  readonly aVerifier?: boolean
}

function avecCache(feries: (annee: number) => string[]) {
  const cache = new Map<number, Set<string>>()
  return (jour: string) => {
    const annee = Number(jour.slice(0, 4))
    let ensemble = cache.get(annee)
    if (!ensemble) {
      ensemble = new Set(feries(annee))
      cache.set(annee, ensemble)
    }
    return ensemble.has(jour)
  }
}

const estFerieFrancais = avecCache(joursFeriesFrancais)

/** Jours ouvrés français : ni samedi, ni dimanche, ni jour férié. Échéances fiscales et sociales. */
export const calendrierFrancais: CalendrierOuvre = {
  nom: 'France',
  estOuvre(jour) {
    const j = jourDeLaSemaine(jour)
    return j !== 0 && j !== 6 && !estFerieFrancais(jour)
  },
}

const estFermeTarget2 = avecCache((annee) => {
  const p = paques(annee)
  return [
    `${annee}-01-01`,
    ajouterJours(p, -2),
    ajouterJours(p, 1),
    `${annee}-05-01`,
    `${annee}-12-25`,
    `${annee}-12-26`,
  ]
})

/**
 * Calendrier bancaire TARGET2 (dates de débit SEPA). À VÉRIFIER : jours de fermeture
 * publiés par la BCE (1er janvier, Vendredi saint, lundi de Pâques, 1er mai, 25 et 26 décembre).
 */
export const calendrierTarget2: CalendrierOuvre = {
  nom: 'TARGET2',
  aVerifier: true,
  estOuvre(jour) {
    const j = jourDeLaSemaine(jour)
    return j !== 0 && j !== 6 && !estFermeTarget2(jour)
  },
}

/** Le jour même s'il est ouvré, sinon le premier jour ouvré qui suit. */
export function jourOuvreSuivant(jour: string, calendrier: CalendrierOuvre): string {
  let j = jour
  while (!calendrier.estOuvre(j)) j = ajouterJours(j, 1)
  return j
}

/** Ajoute (ou retire, si n < 0) n jours ouvrés. */
export function ajouterJoursOuvres(jour: string, n: number, calendrier: CalendrierOuvre): string {
  const pas = n < 0 ? -1 : 1
  let j = jour
  let reste = Math.abs(n)
  while (reste > 0) {
    j = ajouterJours(j, pas)
    if (calendrier.estOuvre(j)) reste--
  }
  return j
}

/** Jour civil à Paris d'un instant donné. */
export function jourAParis(instant: Date): string {
  return formatInTimeZone(instant, FUSEAU, 'yyyy-MM-dd')
}

/** Heure à Paris (« 09:30 ») d'un instant donné. */
export function heureAParis(instant: Date): string {
  return formatInTimeZone(instant, FUSEAU, 'HH:mm')
}

/** « 2026-10-12 » et « 09:30 », heure de Paris : « 2026-10-12T09:30:00+02:00 ». */
export function instantDeParis(jour: string, heure: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) throw new RangeError(`Jour invalide : ${jour}`)
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(heure)) throw new RangeError(`Heure invalide : ${heure}`)
  return formatInTimeZone(
    fromZonedTime(`${jour}T${heure}:00`, FUSEAU),
    FUSEAU,
    "yyyy-MM-dd'T'HH:mm:ssXXX",
  )
}

export { versDate as dateUtc, versJour as jourUtc }
